import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import config from '../utils/config';

const TalleresContext = createContext();
const { API_BASE_URL } = config;
const CACHE_KEY = 'talleresEstado';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Helper para obtener datos del caché si son válidos
 */
const getCachedTalleres = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(`${CACHE_KEY}_timestamp`);

    if (!cached || !timestamp) {
      return null;
    }

    const age = Date.now() - parseInt(timestamp);
    if (age > CACHE_TTL) {
      // Caché expirado, limpiar
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(`${CACHE_KEY}_timestamp`);
      return null;
    }

    console.log(`✅ Usando caché de talleres (${Math.round(age / 1000)}s)`);
    return JSON.parse(cached);
  } catch (err) {
    console.warn('⚠️ Error leyendo caché:', err);
    return null;
  }
};

/**
 * Helper para guardar datos en caché
 */
const setCachedTalleres = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(`${CACHE_KEY}_timestamp`, Date.now().toString());
  } catch (err) {
    console.warn('⚠️ Error guardando caché:', err);
  }
};

/**
 * Provider que mantiene sincronizado el estado global de talleres
 * Usa caché localStorage para evitar requests repetidas
 */
export const TalleresProvider = ({ children }) => {
  const [talleresStatus, setTalleresStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);
  const debounceTimerRef = useRef(null);

  // Función para cargar talleres (con debounce)
  const loadTalleresStatus = async (skipCache = false) => {
    if (isFetchingRef.current) {
      console.log('⏳ Fetch ya en progreso, ignorando request');
      return;
    }

    // Intentar obtener del caché primero
    if (!skipCache) {
      const cached = getCachedTalleres();
      if (cached) {
        setTalleresStatus(cached);
        setLoading(false);
        return;
      }
    }

    try {
      isFetchingRef.current = true;
      console.log('🔄 Cargando estado de talleres desde API...');
      
      const res = await fetch(`${API_BASE_URL}/talleres/admin/listar`);
      if (res.ok) {
        const payload = await res.json();
        const statusMap = {};
        
        // Mapeo de nombres de taller a IDs para talleres con múltiples horarios
        const tallerIdMap = {
          'Herramientas tecnológicas para la práctica contable y fiscal': [25, 26],
          'CONTPAQi Nóminas Básico': [16, 17],
          'CONTPAQi Nóminas - Procesos Especiales': [14, 15],
          'Análisis automático de CFDI': [12, 13],
          'CONTPAQi Procesos Especiales': [8, 9],
          'Taller Declaraciones Anuales': [6, 9],
          'Portal del SAT': [4, 7],
          'CONTPAQi Nivel 2': [1, 6],
          'CONTPAQi desde 0': [10, 11]
        };
        
        if (Array.isArray(payload.data)) {
          payload.data.forEach(t => {
            // Almacenar el taller con su ID principal
            statusMap[t.idTaller] = { 
              idTaller: t.idTaller,
              Nombre: t.Nombre,
              Estado: t.Estado, 
              Activo: t.Activo 
            };
            
            // Si este taller tiene múltiples horarios, mapear todos sus IDs
            const ids = tallerIdMap[t.Nombre];
            if (ids && Array.isArray(ids)) {
              ids.forEach(id => {
                statusMap[id] = { 
                  idTaller: id,
                  Nombre: t.Nombre,
                  Estado: t.Estado, 
                  Activo: t.Activo 
                };
              });
            }
          });
        }
        setTalleresStatus(statusMap);
        setCachedTalleres(statusMap);
        setError(null);
        console.log('✅ Talleres cargados:', Object.keys(statusMap).length);
        
        // 🔥 GUARDAR EN localStorage PARA USAR EN useTallerStatus
        console.log('💾 Guardando talleresEstadoGlobal en localStorage');
        localStorage.setItem('talleresEstadoGlobal', JSON.stringify(statusMap));

      } else {
        console.error('❌ Error cargando talleres:', res.status);
        setError(`Error ${res.status} cargando talleres`);
      }
    } catch (err) {
      console.error('❌ Error en TalleresContext:', err);
      setError(err.message);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  // Cargar talleres al montar (primero intenta caché, luego API)
  useEffect(() => {
    setLoading(true);
    loadTalleresStatus(false); // false = usar caché si disponible
  }, []);

  // Función para refetch manual con debounce (llamada desde AdminPanel después de guardar)
  const refetch = async () => {
    console.log('🔄 Refetch solicitado, aplicando debounce...');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      console.log('🔄 Ejecutando refetch después de debounce...');
      loadTalleresStatus(true); // true = forzar refresh sin caché
    }, 500);
  };

  const value = {
    talleresStatus,
    loading,
    error,
    refetch
  };

  return (
    <TalleresContext.Provider value={value}>
      {children}
    </TalleresContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de talleres
 */
export const useTalleresContext = () => {
  const context = useContext(TalleresContext);
  if (!context) {
    throw new Error('useTalleresContext debe estar dentro de TalleresProvider');
  }
  return context;
};
